<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { PlusIcon, Trash2Icon } from '@lucide/vue';
import { type Sheet } from '@yana/shared';
import { ApiError } from '@/api/client';
import { sheetsApi } from '@/api/sheets';
import { useAuthStore } from '@/stores/auth';
import { useSheetsStore } from '@/stores/sheets';
import { useToastStore } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { ColorPalette } from '@/components/ui/color-picker';
import { Input } from '@/components/ui/input';
const NewSheetDialog = defineAsyncComponent(() => import('./NewSheetDialog.vue'));
const SheetTrashDialog = defineAsyncComponent(() => import('./SheetTrashDialog.vue'));
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';

/**
 * Ярлыки листов по низу экрана — как вкладки рабочей книги.
 *
 * Раньше листы жили пунктом в шапке, и заказчик сказал прямо: должно быть
 * как в таблице. Дело не в красоте — в книге ярлык внизу это ещё и место,
 * где лист создают, красят и переставляют. Меню в шапке ничего из этого
 * не давало.
 *
 * Первый ярлык — основная таблица кандидатов: в книге лист «2026г» стоит
 * первым, и разрывать этот ряд незачем.
 */

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const store = useSheetsStore();
const toasts = useToastStore();

const canCreate = computed(() => auth.can('sheet.create'));
const visibleSheets = computed(() => store.sheets.filter((sheet) => sheet.sourceTable !== 'blacklist'));
const newSheetOpen = ref(false);
const trashOpen = ref(false);

/** Переименование прямо на ярлыке — двойным щелчком, как в книге. */
const renaming = ref<string | null>(null);
const draftName = ref('');
const renameInput = ref<InstanceType<typeof Input>[]>([]);

const isCurrent = (code: string) => route.path === `/sheets/${code}`;
const mainCurrent = computed(() => route.path === '/' || route.path.startsWith('/candidates'));

function canManage(sheet: Sheet): boolean {
  if (sheet.code === 'base' && auth.user?.role !== 'admin') return false;
  return auth.can('sheet.manage') || sheet.ownerId === auth.user?.id;
}

function canDuplicate(sheet: Sheet): boolean {
  return canCreate.value && sheet.kind === 'custom' && (sheet.code !== 'base' || auth.user?.role === 'admin');
}

async function startRename(sheet: Sheet): Promise<void> {
  if (!canManage(sheet)) return;
  renaming.value = sheet.code;
  draftName.value = sheet.name;
  await nextTick();
  (renameInput.value[0]?.$el as HTMLInputElement | undefined)?.select();
}

async function commitRename(sheet: Sheet): Promise<void> {
  const name = draftName.value.trim();
  renaming.value = null;
  if (!name || name === sheet.name) return;
  try {
    await sheetsApi.update(sheet.code, { name });
    await store.loadList(true);
    if (store.code === sheet.code) await store.refreshDetail();
  } catch (e) {
    toasts.error(e instanceof ApiError ? e.message : 'Название не сохранилось');
  }
}

async function paint(sheet: Sheet, color: string | null): Promise<void> {
  try {
    await sheetsApi.update(sheet.code, { color });
    await store.loadList(true);
  } catch (e) {
    toasts.error(e instanceof ApiError ? e.message : 'Цвет не сохранился');
  }
}

async function duplicate(sheet: Sheet): Promise<void> {
  if (!canDuplicate(sheet)) return;
  try {
    const created = await sheetsApi.duplicate(sheet.code);
    await store.loadList(true);
    await router.push(`/sheets/${created.code}`);
    toasts.success('Лист скопирован');
  } catch (e) {
    toasts.error(e instanceof ApiError ? e.message : 'Копия не создалась');
  }
}

async function archive(sheet: Sheet): Promise<void> {
  try {
    await sheetsApi.archive(sheet.code);
    await store.loadList(true);
    if (isCurrent(sheet.code)) await router.push('/');
    toasts.info('Лист в корзине', 'Лист и его строки можно восстановить');
  } catch (e) {
    toasts.error(e instanceof ApiError ? e.message : 'Лист не удалился');
  }
}

// ── Порядок перетаскиванием ───────────────────────────────

const dragged = ref<string | null>(null);

function onDrop(target: Sheet): void {
  const from = dragged.value;
  dragged.value = null;
  if (!from || from === target.code) return;

  const source = store.sheets.find((sheet) => sheet.code === from);
  if (!source || !canManage(source)) return;

  const codes = store.sheets.map((s) => s.code);
  const at = codes.indexOf(from);
  const to = codes.indexOf(target.code);
  if (at < 0 || to < 0) return;

  const next = [...codes];
  next.splice(at, 1);
  next.splice(to, 0, from);

  // Показываем новый порядок сразу: ждать ответа ради перестановки ярлыка
  // означает, что он «отскакивает» назад на время запроса
  store.sheets = next
    .map((code) => store.sheets.find((s) => s.code === code))
    .filter((s): s is Sheet => !!s);

  void sheetsApi.reorder(next).catch(() => {
    toasts.error('Порядок не сохранился');
    void store.loadList(true);
  });
}

watch(
  () => auth.isAuthenticated,
  (yes) => {
    if (yes) void store.loadList();
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="print-hide flex min-h-10 min-w-0 shrink-0 flex-wrap items-center gap-x-1 overflow-hidden border-t border-border bg-app-bg px-2 sm:px-3 md:h-10 md:flex-nowrap"
  >
    <!-- Новый лист: слева от ярлыков, как «+» в книге -->
    <Button
      v-if="canCreate"
      variant="ghost"
      size="icon-xs"
      class="size-7 shrink-0 rounded-md text-muted-foreground"
      aria-label="Новый лист"
      data-tip="Новый лист"
      @click="newSheetOpen = true"
    >
      <PlusIcon class="size-4" />
    </Button>

    <!--
      Корзина листов рядом с «плюсом»: убрать лист можно из меню ярлыка
      одним движением, и вернуть должно быть так же близко
    -->
    <Button
      v-if="canCreate"
      variant="ghost"
      size="icon-xs"
      class="size-7 shrink-0 rounded-md text-muted-foreground"
      aria-label="Корзина листов"
      data-tip="Корзина листов"
      @click="trashOpen = true"
    >
      <Trash2Icon class="size-3.5" />
    </Button>

    <span v-if="canCreate" class="mx-1 h-4 shrink-0 border-r border-border" aria-hidden="true" />
    <nav aria-label="Листы рабочей книги" class="thin-scroll flex min-w-0 flex-1 items-center gap-1 overflow-x-auto py-1">

    <!-- Основная таблица — первый ярлык, как «2026г» в книге -->
    <RouterLink
      to="/"
      class="flex h-7 shrink-0 cursor-pointer items-center rounded-md border px-3 text-[12px] font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      :aria-current="mainCurrent ? 'page' : undefined"
      :class="
        mainCurrent
          ? 'border-border bg-card text-primary shadow-xs'
          : 'border-transparent text-muted-foreground hover:bg-accent/60 hover:text-foreground'
      "
    >
      2026г
    </RouterLink>

    <ContextMenu v-for="sheet in visibleSheets" :key="sheet.code">
      <ContextMenuTrigger as-child>
        <div
          class="shrink-0"
          :draggable="renaming !== sheet.code && canManage(sheet)"
          @dragstart="canManage(sheet) && (dragged = sheet.code)"
          @dragend="dragged = null"
          @dragover.prevent
          @drop.prevent="onDrop(sheet)"
        >
          <!-- Переименование на месте: поле подменяет ярлык, ширина та же -->
          <Input
            v-if="renaming === sheet.code"
            ref="renameInput"
            v-model="draftName"
            class="h-7 w-[140px] rounded-md border-primary bg-background px-2 text-[12px] text-foreground"
            aria-label="Название листа"
            @keydown.enter.prevent="commitRename(sheet)"
            @keydown.esc.prevent="renaming = null"
            @blur="commitRename(sheet)"
          />

          <RouterLink
            v-else
            :to="`/sheets/${sheet.code}`"
            class="flex h-7 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-[12px] font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
            :aria-current="isCurrent(sheet.code) ? 'page' : undefined"
            :class="[
              isCurrent(sheet.code)
                ? 'border-border bg-card text-foreground shadow-xs'
                : 'border-transparent text-muted-foreground hover:bg-accent/60 hover:text-foreground',
              dragged === sheet.code ? 'opacity-40' : '',
            ]"
            :style="
              isCurrent(sheet.code) && sheet.color
                ? { boxShadow: `inset 0 -2px 0 ${sheet.color}` }
                : undefined
            "
            @dblclick.prevent="startRename(sheet)"
          >
            <span
              v-if="sheet.color"
              class="size-2 shrink-0 rounded-full"
              :style="{ background: sheet.color }"
              aria-hidden="true"
            />
            {{ sheet.name }}
          </RouterLink>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent class="w-56">
        <ContextMenuLabel class="text-[11px] font-normal text-muted-foreground">
          {{ sheet.name }}
        </ContextMenuLabel>
        <ContextMenuSeparator />

        <template v-if="canManage(sheet)">
          <ContextMenuItem @select="startRename(sheet)">Переименовать</ContextMenuItem>

          <ContextMenuLabel class="text-[11px] font-normal text-muted-foreground">
            Цвет ярлыка
          </ContextMenuLabel>
          <ColorPalette
            :value="sheet.color"
            @pick="paint(sheet, $event)"
          />
          <ContextMenuItem @select="paint(sheet, null)">Без цвета</ContextMenuItem>
          <ContextMenuSeparator />
        </template>

        <ContextMenuItem
          v-if="canDuplicate(sheet)"
          @select="duplicate(sheet)"
        >
          Создать копию
        </ContextMenuItem>
        <ContextMenuItem
          v-if="canManage(sheet) && (sheet.kind === 'custom' || sheet.code === 'work_objects')"
          variant="destructive"
          @select="archive(sheet)"
        >
          Удалить лист
        </ContextMenuItem>
        <ContextMenuItem v-else-if="sheet.kind === 'system'" disabled>
          {{ sheet.code === 'work_objects' ? 'Нет прав на удаление' : 'Системный лист: удалить нельзя' }}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
    </nav>

    <!--
      Место под состояние таблицы.

      Строка состояния стояла отдельным футером над ярлыками: две полосы
      подряд по 28 и 36 пикселей съедали две строки таблицы, а на ноутбуке
      1366×768 это две из двенадцати. Содержимое приезжает сюда телепортом
      из открытой таблицы — так ярлыки ничего не знают ни про кандидатов,
      ни про листы, и остаются одни на всё приложение.

      На телефоне счётчик и справка занимают компактный ряд под ярлыками:
      им не приходится отбирать место у названий листов. Сам узел остаётся
      одним при любой ширине, поэтому Teleport не теряет свою цель.
    -->
    <div
      id="grid-status"
      class="thin-scroll ml-auto flex h-6 min-w-0 basis-full items-center gap-3 overflow-x-auto border-t border-border pr-1 text-[11px] whitespace-nowrap text-muted-foreground empty:hidden md:max-w-[45%] md:shrink-0 md:basis-auto md:border-l md:border-t-0 md:pl-3"
    />

    <NewSheetDialog v-if="canCreate && newSheetOpen" v-model="newSheetOpen" />
    <SheetTrashDialog v-if="canCreate && trashOpen" v-model="trashOpen" />
  </div>
</template>
